import { Reference, useLazyQuery, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import DateInput from '@components/fields/DateInput';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useMyExpert from '@hooks/useMyExpert';
import {
  CarePlanTaskTemplateFragment as CarePlanTaskTemplateType,
  ExpertGlobalRoleEnum,
  ExpertJourneysSortByEnum,
  ExpertSortByEnum,
  GetCarePlanTaskTemplateDocument,
  GetCarePlanTaskTemplateQuery,
  GetCarePlanTaskTemplateQueryVariables,
  GetExpertJourneysBasicDocument,
  GetExpertJourneysBasicQuery,
  GetExpertJourneysBasicQueryVariables,
  GetExpertsBasicDocument,
  GetExpertsBasicQuery,
  GetExpertsBasicQueryVariables,
  GetJourneyWithExpertRolesDocument,
  GetJourneyWithExpertRolesQuery,
  GetJourneyWithExpertRolesQueryVariables,
  GoalFragment,
  SortDirectionEnum,
  TaskFragment as Task,
  TaskCreateDocument,
  TaskCreateMutation,
  TaskCreateMutationVariables,
  TaskFragmentDoc,
  TaskUpdateDocument,
  TaskUpdateMutation,
  TaskUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { IconName } from '@utils/iconUtils';
import { getCareProfileName } from '@utils/modelUtils';
import { mapSearchToOptions } from '@utils/searchUtils';
import { dateToIso8601 } from '@utils/timeUtils';

type Journey = NonNullable<
  NonNullable<NonNullable<GetExpertJourneysBasicQuery['expertJourneys']['edges']>[number]>['node']
>;

type FormData = {
  carePlanTaskTemplateId?: string | null;
  clientVisible: boolean;
  dueTime: string;
  expertId: string;
  journeyId?: string | null;
  title: string | null;
};

type ClientVisibleOption = {
  iconName: IconName;
  label: string;
  value: boolean;
};

type PropsJourney = {
  carePlanPublishedAt: Date | null;
  id: string;
};

type Props = {
  autofocus?: boolean;
  clientVisibleTaskForm?: boolean;
  goal?: GoalFragment;
  journey?: PropsJourney;
  journeyRefetch?: () => void;
  onCancel: () => void;
  onSuccess: () => void;
  primaryExpertId?: string;
  refetch?: () => void;
  task?: Task | null;
};

const TaskForm = ({
  autofocus,
  clientVisibleTaskForm,
  goal,
  journey,
  journeyRefetch,
  onCancel,
  onSuccess,
  primaryExpertId,
  refetch,
  task
}: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [currentTemplate, setCurrentTemplate] = useState<CarePlanTaskTemplateType>();
  const [currentJourneyOption, setCurrentJourneyOption] = useState<Option>({
    label: task?.client?.fullName
      ? `${task.client.fullName} -- ${getCareProfileName({ intl, journey: task.journey })}`
      : '',
    value: task?.journey?.id ?? ''
  });

  let defaultDueDate = '';
  if (task?.dueTime) {
    defaultDueDate = dateToIso8601(task.dueTime, intl).split('T')[0];
  }

  const getExpertJourneys = useLazyQueryWithPromise<GetExpertJourneysBasicQuery, GetExpertJourneysBasicQueryVariables>(
    GetExpertJourneysBasicDocument
  );

  const [getExperts, getExpertsQuery] = useLazyQuery<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(
    GetExpertsBasicDocument
  );

  const [getJourney, getJourneyQuery] = useLazyQuery<
    GetJourneyWithExpertRolesQuery,
    GetJourneyWithExpertRolesQueryVariables
  >(GetJourneyWithExpertRolesDocument);

  const getCarePlanTaskTemplate = useLazyQueryWithPromise<
    GetCarePlanTaskTemplateQuery,
    GetCarePlanTaskTemplateQueryVariables
  >(GetCarePlanTaskTemplateDocument);

  const [createTask] = useMutation<TaskCreateMutation, TaskCreateMutationVariables>(TaskCreateDocument, {
    update: (store, { data }) => {
      if (data?.taskCreate?.task) {
        const newTask = data.taskCreate.task;
        store.modify({
          fields: {
            clientVisibleTasks(cachedTaskRefs: readonly Reference[]) {
              const newTaskRef = store.writeFragment({
                data: newTask,
                fragment: TaskFragmentDoc,
                fragmentName: 'Task'
              });
              if (newTaskRef) {
                return [...cachedTaskRefs, newTaskRef];
              }
              return [...cachedTaskRefs];
            }
          },
          id: store.identify({ ...journey })
        });
      }
    }
  });
  const [updateTask] = useMutation<TaskUpdateMutation, TaskUpdateMutationVariables>(TaskUpdateDocument);

  const calculateDefaultValues = useCallback(
    () => ({
      carePlanTaskTemplateId: task?.carePlanTaskTemplate?.id ?? null,
      categoryOption: task?.category.categoryOption ?? null,
      categoryOther: task?.category.other ?? null,
      clientVisible: clientVisibleTaskForm ? true : task?.clientVisible ?? false,
      dueTime: defaultDueDate,
      expertId: task?.expert.id ?? primaryExpertId ?? myExpert?.id,
      journeyId: task?.journey?.id ?? journey?.id ?? null,
      title: task?.title ?? ''
    }),
    [
      clientVisibleTaskForm,
      defaultDueDate,
      journey?.id,
      myExpert?.id,
      primaryExpertId,
      task?.carePlanTaskTemplate?.id,
      task?.category.categoryOption,
      task?.category.other,
      task?.clientVisible,
      task?.expert.id,
      task?.journey?.id,
      task?.title
    ]
  );

  const defaultValues = calculateDefaultValues();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      carePlanTaskTemplateId: yup.string().nullable().defined(),
      clientVisible: yup.boolean().required(intl.formatMessage({ id: 'errors.task.clientVisible.blank' })),
      dueTime: yup.string().required(intl.formatMessage({ id: 'errors.task.dueTime.blank' })),
      expertId: yup.string().required(intl.formatMessage({ id: 'errors.task.expertId.blank' })),
      journeyId: yup.string().nullable().defined(),
      title: task?.kind
        ? yup.string().nullable().defined()
        : yup.string().required(intl.formatMessage({ id: 'errors.task.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const carePlanTaskTemplateId = form.watch('carePlanTaskTemplateId');
  const expertId = form.watch('expertId');
  const journeyId = form.watch('journeyId');
  const title = form.watch('title');

  const defaultJourneyOptions = useMemo(
    () => [{ label: intl.formatMessage({ id: 'dictionary.none' }), value: '' }],
    [intl]
  );

  const searchJourneyOptions = useCallback(
    async (searchTerm: string) =>
      new Promise<Option[]>(resolve => {
        if (searchTerm.length > 0 && expertId && !journey?.id) {
          getExpertJourneys({
            variables: {
              expertId,
              search: searchTerm,
              sortBy: ExpertJourneysSortByEnum.client_name,
              sortDirection: SortDirectionEnum.ascending
            }
          }).then(data => {
            if (data.data?.expertJourneys.edges) {
              resolve(
                mapSearchToOptions<Journey>(
                  data.data.expertJourneys.edges.map(i => i?.node ?? null),
                  journey => `${journey?.owner.fullName ?? ''} -- ${getCareProfileName({ intl, journey })}`,
                  model => model?.id ?? '',
                  [{ label: intl.formatMessage({ id: 'dictionary.none' }), value: '' }]
                )
              );
            } else {
              resolve([]);
            }
          });
        } else {
          resolve([{ label: intl.formatMessage({ id: 'dictionary.none' }), value: '' }]);
        }
      }),
    [expertId, getExpertJourneys, intl, journey?.id]
  );

  useEffect(() => {
    if (!journey?.id && myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager) {
      getExperts({
        variables: {
          sortBy: ExpertSortByEnum.name,
          sortDirection: SortDirectionEnum.descending
        }
      });
    }
  }, [getExperts, journey?.id, myExpert?.expertGlobalRole]);

  useEffect(() => {
    if (journeyId) {
      getJourney({
        variables: {
          id: journeyId
        }
      });
    }
  }, [getJourney, journeyId]);

  useEffect(() => {
    if (carePlanTaskTemplateId && carePlanTaskTemplateId !== '') {
      getCarePlanTaskTemplate({
        variables: {
          id: carePlanTaskTemplateId
        }
      }).then(res => {
        if (res.data.carePlanTaskTemplate) {
          setCurrentTemplate(res.data.carePlanTaskTemplate);
        } else {
          setCurrentTemplate(undefined);
        }
      });
    } else {
      setCurrentTemplate(undefined);
    }
  }, [carePlanTaskTemplateId, getCarePlanTaskTemplate]);

  useEffect(() => {
    // While editing, we don't want to override fields since the body could be different then the template
    // If there isn't a current template, and we are not editing then reset the form values
    if (carePlanTaskTemplateId && currentTemplate?.id && currentTemplate?.id !== task?.carePlanTaskTemplate?.id) {
      form.setValue('title', currentTemplate.title);
    } else if (currentTemplate?.id !== task?.carePlanTaskTemplate?.id) {
      form.setValue('title', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlanTaskTemplateId, currentTemplate?.id, currentTemplate?.title]);

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  useEffect(() => {
    if (title === '') {
      form.setValue('carePlanTaskTemplateId', null);
    }
  }, [form, title]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (task?.id) {
      return updateTask({
        variables: {
          ...form.getValues(),
          id: task.id,
          journeyId: journey?.id ? journey.id : form.getValues().journeyId === '' ? null : form.getValues().journeyId
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.taskUpdate,
          intl,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.task'
        });
      });
    }
    const title = form.getValues().title;

    if (!title) return;

    return createTask({
      variables: {
        ...form.getValues(),
        clientVisible: journeyId ? form.getValues().clientVisible : false,
        goalId: goal?.id,
        journeyId: journey?.id ? journey.id : form.getValues().journeyId === '' ? null : form.getValues().journeyId,
        title
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.taskCreate,
        intl,
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.task'
      });
      if (refetch) {
        refetch();
      }
      if (journeyRefetch) {
        journeyRefetch();
      }
    });
  }, [
    task?.id,
    createTask,
    form,
    journeyId,
    goal?.id,
    journey?.id,
    updateTask,
    intl,
    onSuccess,
    refetch,
    journeyRefetch
  ]);

  const expertOptions = useMemo(() => {
    const experts: { label: string; value: string }[] = [];
    if (journeyId) {
      getJourneyQuery.data?.journey.expertRoles?.map(role =>
        experts.push({
          label: role.expert.fullName,
          value: role.expert.id
        })
      );
    } else {
      if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager) {
        getExpertsQuery.data?.experts.edges?.map(expert =>
          experts.push({
            label: expert?.node?.fullName as string,
            value: expert?.node?.id as string
          })
        );
      } else if (myExpert) {
        experts.push({
          label: myExpert.fullName,
          value: myExpert.id
        });
      }
    }
    return experts;
  }, [getExpertsQuery.data?.experts.edges, getJourneyQuery.data?.journey.expertRoles, journeyId, myExpert]);

  const clientVisibileOptions: ClientVisibleOption[] = useMemo(() => {
    const options = [true, false];
    return options.map(option => ({
      iconName: option ? 'eye' : 'eyeSlash',
      label: intl.formatMessage({
        id: `models.tasks.clientVisibleOptions.${option ? 'yes' : 'no'}`
      }),
      value: option
    }));
  }, [intl]);

  return (
    <Form data-test="medication-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <>
        <BaseErrors errors={baseErrors} />
        <div className={task?.kind ? 'ion-hide' : undefined}>
          <Input
            autofocus={!task?.kind ? autofocus : false}
            control={form.control}
            error={form.formState.errors.title}
            label={intl.formatMessage({ id: 'forms.task.titleLabel' })}
            name="title"
            required
            spellcheck
          />
        </div>
        <div className={journey?.id ? 'ion-hide' : undefined}>
          <AsyncSelect<FormData>
            autofocus={journey?.id ? autofocus : false}
            control={form.control}
            defaultOptions={defaultJourneyOptions}
            error={form.formState.errors.journeyId}
            label={intl.formatMessage({ id: 'forms.task.journeyIdLabel' })}
            name="journeyId"
            onSearch={searchJourneyOptions}
            onSelection={(option: Option) => {
              setCurrentJourneyOption(option);
            }}
            value={currentJourneyOption}
          />
        </div>
        <Select
          control={form.control}
          error={form.formState.errors.expertId}
          label={intl.formatMessage({ id: 'forms.task.expertIdLabel' })}
          name="expertId"
          options={expertOptions}
          required
        />
        <DateInput
          control={form.control}
          error={form.formState.errors.dueTime}
          futureOnly
          label={intl.formatMessage({ id: 'forms.task.dueTimeLabel' })}
          name="dueTime"
          required
        />
        <div className={journeyId && !clientVisibleTaskForm && !task ? '' : 'ion-hide'}>
          <BooleanRadioButtonGroup
            control={form.control}
            error={form.formState.errors.clientVisible}
            inTracker
            label={intl.formatMessage({ id: 'forms.task.clientVisibleLabel' })}
            name="clientVisible"
            options={clientVisibileOptions}
            required
            smallerLabel
          />
        </div>
        <div>
          <FormButtons hookForm={form} onCancel={onCancel} />
        </div>
      </>
    </Form>
  );
};

export default TaskForm;
